<template>
  <div class="drag-match-host-interface">

    <!-- Drag-Match Control Header -->
    <div class="drag-match-control-header mb-4">
      <div class="row align-items-center">
        <div class="col">
          <h3 class="mb-0">
            <i class="bi bi-puzzle"></i>
            Drag-Match Control
          </h3>
          <small class="text-muted">{{ dragMatchConfig?.name || 'Drag-Match Game' }}</small>
        </div>
        <div class="col-auto">
          <div class="game-status badge fs-6" :class="statusBadgeClass">
            {{ gameStatus }}
          </div>
        </div>
      </div>
    </div>

    <!-- Screen Navigation -->
    <div class="screen-navigation mb-4">


          <div class="current-screen-info mb-3">
            <div class="row align-items-center">
              <div class="col">
                <strong>Current Screen: {{ currentScreenIndex + 1 }} / {{ totalScreens }}</strong>
                <br>
                <small class="text-muted">
                  {{ currentScreen?.screenType === 'question' ? 'Question' : 'Interlude' }}
                  {{ currentScreen?.title ? `: ${currentScreen.title}` : '' }}
                </small>
              </div>
              <div class="col-auto">
                <div class="btn-group" role="group">
                  <button
                    @click="previousScreen"
                    :disabled="currentScreenIndex === 0 || gameStatus === 'stopped'"
                    class="btn btn-outline-secondary"
                  >
                    <i class="bi bi-chevron-left"></i>
                    Previous
                  </button>
                  <button
                    @click="nextScreen"
                    :disabled="currentScreenIndex >= totalScreens - 1 || gameStatus === 'stopped'"
                    class="btn btn-outline-secondary"
                  >
                    Next <i class="bi bi-chevron-right"></i>
                  </button>

                </div>

                <button
                    @click="revealAnswers"
                    :disabled="!canRevealAnswers || gameStatus === 'stopped'"
                    class="btn btn-outline-secondary ms-2"
                    title="Reveal correct answers to players"
                  >
                    <i class="bi bi-eye"></i> Reveal
                  </button>
              </div>
            </div>
          </div>

          <!-- Screen Timeline -->
          <div class="screen-timeline">
            <div class="progress mb-2" style="height: 8px;">
              <div
                class="progress-bar"
                :style="{ width: `${screenProgress}%` }"
              ></div>
            </div>
            <div class="screen-indicators">
              <button
                v-for="(screen, index) in dragMatchConfig?.screens || []"
                :key="index"
                @click="goToScreen(index)"
                :class="[
                  'screen-indicator',
                  { 'current': index === currentScreenIndex },
                  { 'completed': index < currentScreenIndex },
                  { 'question': screen.screenType === 'question' },
                  { 'interlude': screen.screenType === 'interlude' }
                ]"
                :title="getScreenTooltip(screen, index)"
              >
                <i :class="getScreenIcon(screen)"></i>
              </button>
            </div>
          </div>

    </div>

    <!-- Game Control Buttons -->
    <div class="game-controls mb-4">

          <h5>
            <i class="bi bi-play-circle"></i>
            Game Controls
          </h5>

          <div class="btn-group w-100" role="group">
            <button
              v-if="gameStatus === 'stopped'"
              @click="startGame"
              class="btn btn-success"
            >
              <i class="bi bi-play-fill"></i>
              Start Game
            </button>
            <button
              v-if="gameStatus === 'playing'"
              @click="pauseGame"
              class="btn btn-warning"
            >
              <i class="bi bi-pause-fill"></i>
              Pause
            </button>
            <button
              v-if="gameStatus === 'paused'"
              @click="resumeGame"
              class="btn btn-primary"
            >
              <i class="bi bi-play-fill"></i>
              Resume
            </button>
            <button
              v-if="gameStatus !== 'stopped'"
              @click="stopGame"
              class="btn btn-danger"
            >
              <i class="bi bi-stop-fill"></i>
              Stop
            </button>
          </div>

    </div>

    <!-- Player Answers Monitor -->
    <div class="player-answers-monitor mb-4">

          <h5>
            <i class="bi bi-people"></i>
            Player Progress
            <span class="badge bg-secondary">{{ answeredCount }}/{{ connectedPlayers.length }}</span>
          </h5>

          <div class="players-list">
            <div class="row">
              <div v-for="player in connectedPlayers" :key="player.id" class="col-md-6 col-lg-4 mb-2">
                <div :class="getPlayerCardClass(player.id)" class="player-card">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="player-info">
                      <div class="player-name">{{ player.name || player.pseudo }}</div>
                      <div class="player-status">
                        <i :class="getPlayerStatusIcon(player.id)"></i>
                      </div>
                    </div>
                    <div class="player-score">
                      <small class="text-muted">Score: {{ getPlayerScore(player.id) }}</small>
                    </div>
                  </div>
                  <small class="player-answer text-muted">
                    {{ getPlayerAnswerDisplay(player.id) }}
                  </small>
                </div>
              </div>
            </div>
          </div>

    </div>

    <!-- Game Progress Summary -->
    <div class="game-summary">

          <h5 >
            <i class="bi bi-graph-up"></i>
            Game Summary
          </h5>

          <div class="row text-center">
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ currentScreenIndex + 1 }}</div>
                <div class="stat-label">Current Screen</div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ answeredCount }}</div>
                <div class="stat-label">Players Answered</div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ totalScreens }}</div>
                <div class="stat-label">Total Screens</div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ Math.round(screenProgress) }}%</div>
                <div class="stat-label">Progress</div>
              </div>
            </div>
          </div>

    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import type { DragMatchConfig, DragMatchScreen } from '@/games/drag-match/models/DragMatchConfig'

// Props
interface Props {
  dragMatchConfig: DragMatchConfig
  connectedPlayers: Array<{ id: string; name?: string; pseudo?: string }>
  signaling: any
  hostSignaling?: any
  currentScreenIndex?: number
  gameStatus?: 'stopped' | 'playing' | 'paused'
  playerAnswers?: Map<string, any>
  playerScores?: Map<string, number>
}

const props = withDefaults(defineProps<Props>(), {
  currentScreenIndex: 0,
  gameStatus: 'stopped',
  playerAnswers: () => new Map(),
  playerScores: () => new Map()
})

// Emits
const emit = defineEmits<{
  'screen-changed': [screenIndex: number]
  'game-started': []
  'game-paused': []
  'game-resumed': []
  'game-stopped': []
  'reveal-answers': [screenIndex: number, correctAnswers: any]
}>()

// Reactive state - use props for shared state
const gameStatus = computed(() => props.gameStatus || 'stopped')
const currentScreenIndex = computed(() => props.currentScreenIndex || 0)
const playerAnswers = computed(() => props.playerAnswers || new Map())
const playerScores = computed(() => props.playerScores || new Map())

// Computed properties
const totalScreens = computed(() => props.dragMatchConfig?.screens?.length || 0)

const currentScreen = computed(() => {
  const screens = props.dragMatchConfig?.screens
  if (screens && currentScreenIndex.value >= 0 && currentScreenIndex.value < screens.length) {
    return screens[currentScreenIndex.value]
  }
  return null
})

const screenProgress = computed(() => {
  if (totalScreens.value === 0) return 0
  return ((currentScreenIndex.value + 1) / totalScreens.value) * 100
})

const answeredCount = computed(() => {
  return Array.from(playerAnswers.value.values()).filter(
    answer => answer.screenIndex === currentScreenIndex.value
  ).length
})

const statusBadgeClass = computed(() => {
  switch (gameStatus.value) {
    case 'playing': return 'bg-success'
    case 'paused': return 'bg-warning'
    default: return 'bg-secondary'
  }
})

const canRevealAnswers = computed(() => {
  return true;
  console.log('Checking if can reveal answers...', currentScreen.value?.screenType, answeredCount.value, gameStatus.value)
  // Can reveal answers if we're on a question screen and some players have answered
  return currentScreen.value?.screenType === 'question' &&
         answeredCount.value > 0 &&
         gameStatus.value !== 'stopped'
})

// Methods
function startGame() {
  if (props.hostSignaling?.startGame) {
    props.hostSignaling.startGame(0, 'drag-and-match', props.dragMatchConfig)
  }
  emit('game-started')
  emit('screen-changed', 0)
}

function pauseGame() {
  if (props.hostSignaling?.pauseGame) {
    props.hostSignaling.pauseGame()
  }
  emit('game-paused')
}

function resumeGame() {
  if (props.hostSignaling?.resumeGame) {
    props.hostSignaling.resumeGame()
  }
  emit('game-resumed')
}

function stopGame() {
  if (props.hostSignaling?.endGame) {
    props.hostSignaling.endGame(0)
  }
  emit('game-stopped')
}

function previousScreen() {
  if (props.hostSignaling?.previousScreen) {
    props.hostSignaling.previousScreen()
  }
  emit('screen-changed', Math.max(0, currentScreenIndex.value - 1))
}

function nextScreen() {
  if (props.hostSignaling?.nextScreen) {
    props.hostSignaling.nextScreen()
  }
  emit('screen-changed', Math.min(totalScreens.value - 1, currentScreenIndex.value + 1))
}

function goToScreen(index: number) {
  if (index >= 0 && index < totalScreens.value) {
    if (props.hostSignaling?.changeScreen) {
      props.hostSignaling.changeScreen(0, index, props.dragMatchConfig.screens[index])
    }
    emit('screen-changed', index)
  }
}

function revealAnswers() {
  if (!canRevealAnswers.value) return

  // Get the correct answers for the current screen
  const screen = currentScreen.value
  if (!screen || screen.screenType !== 'question' || !('slots' in screen)) return

  // Build correct answers mapping for drag-match
  const correctAnswers: Record<string, string> = {}

  // Map each slot to its correct answer
  if ('slots' in screen) {
    (screen as any).slots?.forEach((slot: any) => {
      if (slot.correctAnswer) {
        correctAnswers[slot.slotId] = slot.correctAnswer
      }
    })
  }

  emit('reveal-answers', currentScreenIndex.value, correctAnswers)
}

function getScreenIcon(screen: DragMatchScreen): string {
  return screen.screenType === 'question' ? 'bi bi-puzzle' : 'bi bi-info-circle'
}

function getScreenTooltip(screen: DragMatchScreen, index: number): string {
  const type = screen.screenType === 'question' ? 'Question' : 'Interlude'
  const title = screen.title ? `: ${screen.title}` : ''
  return `Screen ${index + 1} - ${type}${title}`
}

function getPlayerCardClass(playerId: string): string {
  const hasAnswered = playerAnswers.value.get(playerId)?.screenIndex === currentScreenIndex.value
  return hasAnswered ? 'player-card answered' : 'player-card waiting'
}

function getPlayerStatusIcon(playerId: string): string {
  const hasAnswered = playerAnswers.value.get(playerId)?.screenIndex === currentScreenIndex.value
  return hasAnswered ? 'bi bi-check-circle text-success' : 'bi bi-clock text-warning'
}

function getPlayerAnswerDisplay(playerId: string): string {
  const playerAnswer = playerAnswers.value.get(playerId)
  if (!playerAnswer || playerAnswer.screenIndex !== currentScreenIndex.value) {
    return 'Waiting for answer...'
  }

  const answerEntries = Object.entries(playerAnswer.answer)
  if (answerEntries.length === 0) {
    return 'No placements made'
  }

  return `${answerEntries.length} option(s) placed`
}

function getPlayerScore(playerId: string): number {
  return playerScores.value.get(playerId) || 0
}

function updatePlayerAnswer(playerId: string, answer: Record<string, string>) {
  playerAnswers.value.set(playerId, {
    answer,
    screenIndex: currentScreenIndex.value,
    timestamp: Date.now()
  })
}

function updatePlayerScore(playerId: string, score: number) {
  playerScores.value.set(playerId, score)
}

function getCurrentScreenData() {
  return {
    screen: currentScreen.value,
    screenIndex: currentScreenIndex.value,
    totalScreens: totalScreens.value
  }
}

// Watch for external updates
watch(() => props.connectedPlayers, (newPlayers) => {
  // Initialize scores for new players
  newPlayers.forEach(player => {
    if (!playerScores.value.has(player.id)) {
      playerScores.value.set(player.id, 0)
    }
  })
}, { deep: true })

// Expose public methods
defineExpose({
  startGame,
  pauseGame,
  resumeGame,
  stopGame,
  previousScreen,
  nextScreen,
  goToScreen,
  updatePlayerAnswer,
  updatePlayerScore,
  getCurrentScreenData
})

// Lifecycle
onMounted(() => {
  console.log('DragMatchHostInterface mounted with config:', props.dragMatchConfig)
})
</script>

<style scoped>
.drag-match-host-interface {
  padding: 20px;
}

.drag-match-control-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 15px;
}

.screen-navigation .current-screen-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.screen-timeline {
  margin-top: 15px;
}

.screen-indicators {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.screen-indicator {
  width: 40px;
  height: 40px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.screen-indicator:hover {
  border-color: #6c757d;
  transform: translateY(-1px);
}

.screen-indicator.current {
  border-color: #0d6efd;
  background: #0d6efd;
  color: white;
}

.screen-indicator.completed {
  border-color: #198754;
  background: #198754;
  color: white;
}

.screen-indicator.question {
  /* Default styling for question indicators */
  border-color: #6f42c1;
  color: #6f42c1;
}

.screen-indicator.interlude {
  background: #ffc107;
  border-color: #ffc107;
  color: #000;
}

.players-list {
  max-height: 300px;
  overflow-y: auto;
}

.player-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px;
  transition: all 0.2s ease;
}

.player-card.answered {
  background: #d1edff;
  border-color: #0d6efd;
}

.player-card.waiting {
  background: #fff3cd;
  border-color: #ffc107;
}

.player-name {
  font-weight: 500;
  color: #495057;
}

.player-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-stat {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #495057;
}

.stat-label {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .drag-match-host-interface {
    padding: 15px;
  }

  .screen-indicators {
    justify-content: center;
  }

  .screen-indicator {
    width: 35px;
    height: 35px;
  }

  .players-list .col-md-6,
  .players-list .col-lg-4 {
    flex: 1 1 100%;
  }
}
</style>
